/**
 * ROAR-4945 (ROAR-4899 AC8) — the single source of truth for how an auth method is named in the
 * Login Activity pane's "SIGNED IN WITH" column.
 *
 * `GET /account/login-activity` returns `device`, but that field carries
 * `user_login_histories.logged_in_using` — the auth **METHOD** (`'password'`, `'google'`,
 * `'email_code'`, …), not a browser/OS string. The collection captures no user-agent at all, so a
 * value like "Chrome · macOS" was never something this endpoint could return; the pane's original
 * DEVICE heading and its raw rendering were both built against a shape that does not exist.
 *
 * A registry rather than a `switch` or an inline map at the call site, per CLAUDE.md: adding a
 * method is a one-entry change here, and `loginAuthMethods.test.js` asserts each wire value by
 * name, so a new backend write path that forgets a label fails in review rather than showing a
 * user a raw `snake_case` token.
 *
 * The keys are the values `api-backend-provapt` actually writes today:
 * `post.userLogin.js` (`password`), `post.verifyCode.js` / `post.signUp.js` (`email_code`),
 * `finalizeIdpLogin.js` / `finalizeIdpSignup.js` (the IdP `provider`, e.g. `google`),
 * `finalizeOktaLogin.js` (`okta`) and `post.inviteTeam.js` (`invite_link`).
 */
export const LOGIN_AUTH_METHOD_LABELS = Object.freeze({
  password: 'Password',
  email_code: 'Email code',
  google: 'Google',
  microsoft: 'Microsoft',
  okta: 'Okta',
  invite_link: 'Invite link',
});

/** What an empty cell shows, matching the table's other "no value" cells. */
const NO_VALUE = '-';

/**
 * Humanises an unregistered method: `saml_sso` → `Saml sso`.
 *
 * Deliberately NOT title-case-every-word — an unknown value is a raw backend token, and rendering
 * it lightly cleaned reads as provisional rather than as a designed label, which is what it is.
 *
 * @param {string} value A trimmed, non-empty wire value.
 * @returns {string}
 */
/**
 * An OWN-property lookup — `LABELS['constructor']` would otherwise return a FUNCTION, which React
 * refuses to render as a child. See the same helper in `paymentMethodLabels.js`.
 *
 * @param {object} registry A frozen label map.
 * @param {string} key A trimmed wire value.
 * @returns {string|undefined}
 */
const lookup = (registry, key) =>
  Object.prototype.hasOwnProperty.call(registry, key) ? registry[key] : undefined;

const humanise = (value) => {
  const spaced = value.replace(/_/g, ' ').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * The label for a login-history row's auth method.
 *
 * `finalizeIdpLogin` writes whatever `provider` it was handed, so the backend can legitimately
 * introduce a method before this registry learns about it. An unknown value therefore degrades to
 * a readable form rather than rendering blank — a blank cell reads as "we lost your data", which
 * is worse than an unpolished label on a security screen.
 *
 * @param {string} [method] `logged_in_using`, as the api layer maps it onto `device`.
 * @returns {string} The label, or `'-'` when the row carries no method.
 */
export const resolveLoginAuthMethodLabel = (method) => {
  const value = String(method ?? '').trim();
  if (!value) return NO_VALUE;
  return lookup(LOGIN_AUTH_METHOD_LABELS, value) ?? humanise(value);
};
